import {
  type FreigabeStatus,
  type SchemaElementArt,
  parseAbleitungsmodifikationenRepraesentation,
  parseAbleitungsmodifikationenStruktur,
  parseAnzahl,
  parseFreigabeStatus,
  parseSchemaElementArt,
  parseXmlDocument,
} from "../common";
import { InternalParserError } from "../errors";
import { Table } from "../table";
import { parseCode, parseDate } from "../util";
import {
  type BaseData,
  type ChildRef,
  type Constraints,
  type DataField,
  type DataGroup,
  type ElementData,
  type ElementIdentifikation,
  type Keyword,
  type MessageHeader,
  type NormReference,
  type Relation,
  type Rule,
  type Schema,
  type SchemaMessage,
  parseDatentyp,
  parseFeldart,
  parseGruppenart,
  parseRegelTyp,
  parseRelationType,
  parseVorbefuellung,
} from "./schema";

const SCHEMA_MESSAGE = "xdatenfelder.stammdatenschema.0102";

interface Context {
  dataGroups: Table<DataGroup>;
  dataFields: Table<DataField>;
  rules: Table<Rule>;
}

interface BaseContainer {
  identification: ElementIdentifikation | undefined;
  name: string | undefined;
  description: string | undefined;
  definition: string | undefined;
  releaseState: FreigabeStatus | undefined;
  stateSetAt: Date | undefined;
  stateSetBy: string | undefined;
  validSince: Date | undefined;
  validUntil: Date | undefined;
  versionHint: string | undefined;
  publishedAt: Date | undefined;
  lastChangedAt: Date | undefined;
  normReferences: NormReference[];
  keywords: Keyword[];
  relations: Relation[];
}

interface ElementContainer extends BaseContainer {
  inputLabel: string | undefined;
  outputLabel: string | undefined;
  elementType: SchemaElementArt | undefined;
  inputHelp: string | undefined;
  outputHelp: string | undefined;
}

function createBaseContainer(): BaseContainer {
  return {
    identification: undefined,
    name: undefined,
    description: undefined,
    definition: undefined,
    releaseState: undefined,
    stateSetAt: undefined,
    stateSetBy: undefined,
    validSince: undefined,
    validUntil: undefined,
    versionHint: undefined,
    publishedAt: undefined,
    lastChangedAt: undefined,
    normReferences: [],
    keywords: [],
    relations: [],
  };
}

function createElementContainer(): ElementContainer {
  return {
    identification: undefined,
    name: undefined,
    description: undefined,
    definition: undefined,
    releaseState: undefined,
    stateSetAt: undefined,
    stateSetBy: undefined,
    validSince: undefined,
    validUntil: undefined,
    versionHint: undefined,
    publishedAt: undefined,
    lastChangedAt: undefined,
    normReferences: [],
    keywords: [],
    relations: [],
    inputLabel: undefined,
    outputLabel: undefined,
    elementType: undefined,
    inputHelp: undefined,
    outputHelp: undefined,
  };
}

function parseBaseData(container: BaseContainer): BaseData {
  if (!container.identification) {
    throw new Error("Missing <identifikation>");
  }

  const { id, version } = container.identification;

  if (!container.name) {
    throw new Error("Missing <name>");
  }

  if (!container.lastChangedAt) {
    throw new Error("Missing <letzteAenderung>");
  }

  if (!container.releaseState) {
    throw new Error("Missing <freigabestatus>");
  }

  return {
    identifier: `${id}:${version}`,
    id,
    version,
    name: container.name,
    description: container.description,
    definition: container.definition,
    releaseState: container.releaseState,
    stateSetAt: container.stateSetAt,
    stateSetBy: container.stateSetBy,
    validSince: container.validSince,
    validUntil: container.validUntil,
    versionHint: container.versionHint,
    publishedAt: container.publishedAt,
    lastChangedAt: container.lastChangedAt,
    normReferences: container.normReferences,
    keywords: container.keywords,
    relations: container.relations,
  };
}

function parseElementData(container: ElementContainer): ElementData {
  if (!container.identification) {
    throw new Error("Missing <identifikation>");
  }

  const { id, version } = container.identification;

  if (!container.name) {
    throw new Error("Missing <name>");
  }

  if (!container.lastChangedAt) {
    throw new Error("Missing <letzteAenderung>");
  }

  if (!container.releaseState) {
    throw new Error("Missing <freigabestatus>");
  }

  if (!container.inputLabel) {
    throw new Error("Missing <bezeichnungEingabe>");
  }

  if (!container.elementType) {
    throw new Error("Missing <schemaelementart>");
  }

  return {
    identifier: `${id}:${version}`,
    id,
    version,
    name: container.name,
    description: container.description,
    definition: container.definition,
    releaseState: container.releaseState,
    stateSetAt: container.stateSetAt,
    stateSetBy: container.stateSetBy,
    validSince: container.validSince,
    validUntil: container.validUntil,
    versionHint: container.versionHint,
    publishedAt: container.publishedAt,
    lastChangedAt: container.lastChangedAt,
    normReferences: container.normReferences,
    keywords: container.keywords,
    relations: container.relations,
    inputLabel: container.inputLabel,
    outputLabel: container.outputLabel,
    elementType: container.elementType,
    inputHelp: container.inputHelp,
    outputHelp: container.outputHelp,
  };
}

function getTextContent(element: Element): string {
  return element.textContent || "";
}

function getAttribute(element: Element, name: string): string | undefined {
  const attr = element.getAttribute(name);
  return attr !== null ? attr : undefined;
}

function parseIdentification(element: Element): ElementIdentifikation {
  let id: string | undefined;
  let version: string | undefined;

  for (const child of element.children) {
    switch (child.localName) {
      case "id":
        id = getTextContent(child);
        break;
      case "version":
        version = getTextContent(child);
        break;
    }
  }

  if (!id) {
    throw InternalParserError.missingValue("id");
  }

  return { id, version };
}

function parseBaseContainer(element: Element, container: BaseContainer): void {
  for (const child of element.children) {
    switch (child.localName) {
      case "identifikation":
        container.identification = parseIdentification(child);
        break;
      case "name":
        container.name = getTextContent(child);
        break;
      case "beschreibung":
        container.description = getTextContent(child);
        break;
      case "definition":
        container.definition = getTextContent(child);
        break;
      case "freigabestatus":
        container.releaseState = parseCode(child, parseFreigabeStatus);
        break;
      case "statusGesetztAm":
        container.stateSetAt = parseDate(getTextContent(child));
        break;
      case "statusGesetztDurch":
        container.stateSetBy = getTextContent(child);
        break;
      case "gueltigAb":
        container.validSince = parseDate(getTextContent(child));
        break;
      case "gueltigBis":
        container.validUntil = parseDate(getTextContent(child));
        break;
      case "versionshinweis":
        container.versionHint = getTextContent(child);
        break;
      case "veroeffentlichungsdatum":
        container.publishedAt = parseDate(getTextContent(child));
        break;
      case "letzteAenderung":
        container.lastChangedAt = parseDate(getTextContent(child));
        break;
      case "bezug":
        {
          const link = getAttribute(child, "link");
          const value = getTextContent(child);
          if (value || link) {
            container.normReferences.push({ value, link });
          } else if (link) {
            throw new InternalParserError(
              "<bezug> with a link attribute needs a non-empty content",
            );
          }
        }
        break;
      case "stichwort":
        {
          const uri = getAttribute(child, "uri");
          const keywordValue = getTextContent(child);
          if (keywordValue) {
            container.keywords.push({ value: keywordValue, uri });
          } else if (uri) {
            throw new InternalParserError(
              "<stichwort> with an uri attribute needs a non-empty content",
            );
          }
        }
        break;
      case "relation":
        {
          let praedikat: any;
          let objekt: any;

          for (const relationChild of Array.from(child.children)) {
            if (relationChild.localName === "praedikat") {
              praedikat = parseCode(relationChild, parseRelationType);
            } else if (relationChild.localName === "objekt") {
              objekt = parseIdentification(relationChild);
            }
          }

          if (praedikat && objekt) {
            container.relations.push({ praedikat, objekt });
          } else {
            if (!praedikat) throw InternalParserError.missingValue("praedikat");
            if (!objekt) throw InternalParserError.missingValue("objekt");
          }
        }
        break;
    }
  }
}

function parseElementContainer(
  element: Element,
  container: ElementContainer,
): void {
  parseBaseContainer(element, container);

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "schemaelementart":
        container.elementType = parseCode(child, parseSchemaElementArt);
        break;
      case "bezeichnungEingabe":
        container.inputLabel = getTextContent(child);
        break;
      case "bezeichnungAusgabe":
        container.outputLabel = getTextContent(child);
        break;
      case "hilfetextEingabe":
        container.inputHelp = getTextContent(child);
        break;
      case "hilfetextAusgabe":
        container.outputHelp = getTextContent(child);
        break;
    }
  }
}

function parseConstraints(element: Element): Constraints {
  const constraints: Constraints = {};

  const minLength = getAttribute(element, "minLength");
  if (minLength !== undefined) {
    constraints.minLength = Number.parseInt(minLength);
  }

  const maxLength = getAttribute(element, "maxLength");
  if (maxLength !== undefined) {
    constraints.maxLength = Number.parseInt(maxLength);
  }

  constraints.minValue = getAttribute(element, "minValue");
  constraints.maxValue = getAttribute(element, "maxValue");
  constraints.pattern = getAttribute(element, "pattern");

  const value = getTextContent(element);
  if (value) {
    constraints.value = value;
  }

  return constraints;
}

function parseDataField(element: Element, context: Context): DataField {
  const container = createElementContainer();
  parseElementContainer(element, container);

  const data = parseElementData(container);

  let inputType: any;
  let dataType: any;
  let fillType: any;
  let constraints: Constraints = {};
  let content: string | undefined;
  let codeKey: string | undefined;
  let nameKey: string | undefined;
  let helpKey: string | undefined;
  let maxSize: number | undefined;
  const mediaTypes: string[] = [];
  const rules: ElementIdentifikation[] = [];

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "feldart":
        inputType = parseCode(child, parseFeldart);
        break;
      case "datentyp":
        dataType = parseCode(child, parseDatentyp);
        break;
      case "vorbefuellung":
        fillType = parseCode(child, parseVorbefuellung);
        break;
      case "praezisierung":
        constraints = parseConstraints(child);
        break;
      case "inhalt":
        content = getTextContent(child);
        break;
      case "codeKey":
        codeKey = getTextContent(child);
        break;
      case "nameKey":
        nameKey = getTextContent(child);
        break;
      case "helpKey":
        helpKey = getTextContent(child);
        break;
      case "maxSize":
        maxSize = Number.parseInt(getTextContent(child));
        break;
      case "mediaType":
        mediaTypes.push(getTextContent(child));
        break;
      case "regel":
        {
          const rule = parseRule(child, context);
          rules.push({ id: rule.id, version: rule.version });
        }
        break;
    }
  }

  if (!inputType) throw new Error("Missing <feldart>");
  if (!dataType) throw new Error("Missing <datentyp>");
  if (!fillType) throw new Error("Missing <vorbefuellung>");

  const dataField: DataField = {
    ...data,
    inputType,
    dataType,
    fillType,
    constraints,
    content,
    codeKey,
    nameKey,
    helpKey,
    maxSize,
    mediaTypes,
    rules,
    values: [],
  };

  context.dataFields.insert(dataField);
  return dataField;
}

function parseDataGroup(element: Element, context: Context): DataGroup {
  const container = createElementContainer();
  parseElementContainer(element, container);

  const data = parseElementData(container);

  let art: any;
  const rules: ElementIdentifikation[] = [];
  const children: ChildRef[] = [];

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "art":
        art = parseCode(child, parseGruppenart);
        break;
      case "regel":
        {
          const rule = parseRule(child, context);
          rules.push({ id: rule.id, version: rule.version });
        }
        break;
      case "struktur":
        {
          const childRef = parseStructure(child, context);
          children.push(childRef);
        }
        break;
    }
  }

  const dataGroup: DataGroup = {
    ...data,
    art,
    rules,
    children,
  };

  context.dataGroups.insert(dataGroup);
  return dataGroup;
}

function parseStructure(element: Element, context: Context): ChildRef {
  let anzahl: any;
  const normReferences: NormReference[] = [];
  let childRef: ChildRef | undefined;

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "anzahl":
        anzahl = parseAnzahl(getTextContent(child));
        break;
      case "bezug":
        {
          const link = getAttribute(child, "link");
          const value = getTextContent(child);
          if (value || link) {
            normReferences.push({ value, link });
          } else if (link) {
            throw new InternalParserError(
              "<bezug> with a link attribute needs a non-empty content",
            );
          }
        }
        break;
      case "enthaelt":
        {
          let dataField: DataField | undefined;
          let dataGroup: DataGroup | undefined;

          for (const containsChild of Array.from(child.children)) {
            if (containsChild.localName === "datenfeld") {
              dataField = parseDataField(containsChild, context);
            } else if (containsChild.localName === "datenfeldgruppe") {
              dataGroup = parseDataGroup(containsChild, context);
            }
          }

          if (dataField) {
            childRef = {
              type: "dataField",
              element: { id: dataField.id, version: dataField.version },
              anzahl,
              normReferences,
            };
          } else if (dataGroup) {
            childRef = {
              type: "dataGroup",
              element: { id: dataGroup.id, version: dataGroup.version },
              anzahl,
              normReferences,
            };
          } else {
            throw InternalParserError.missingValue(
              "datenfeld | datenfeldgruppe",
            );
          }
        }
        break;
    }
  }

  if (!anzahl) {
    throw InternalParserError.missingValue("anzahl");
  }

  if (!childRef) {
    throw InternalParserError.missingValue("enthaelt");
  }

  return childRef;
}

function parseRule(element: Element, context: Context): Rule {
  let id: string | undefined;
  let version: string | undefined;
  let name: string | undefined;
  let description: string | undefined;
  let freeFormDefinition: string | undefined;
  let creator: string | undefined;
  let lastChangedAt: Date | undefined;
  let type: any;
  let script: string | undefined;
  const normReferences: NormReference[] = [];
  const keywords: Keyword[] = [];

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "identifikation":
        {
          const identification = parseIdentification(child);
          id = identification.id;
          version = identification.version;
        }
        break;
      case "name":
        name = getTextContent(child);
        break;
      case "beschreibung":
        description = getTextContent(child);
        break;
      case "freitextRegel":
        freeFormDefinition = getTextContent(child);
        break;
      case "fachlicherErsteller":
        creator = getTextContent(child);
        break;
      case "letzteAenderung":
        lastChangedAt = parseDate(getTextContent(child));
        break;
      case "typ":
        type = parseCode(child, parseRegelTyp);
        break;
      case "skript":
        script = getTextContent(child);
        break;
      case "bezug":
        {
          const link = getAttribute(child, "link");
          const value = getTextContent(child);
          if (value || link) {
            normReferences.push({ value, link });
          } else if (link) {
            throw new InternalParserError(
              "<bezug> with a link attribute needs a non-empty content",
            );
          }
        }
        break;
      case "stichwort":
        {
          const uri = getAttribute(child, "uri");
          const keywordValue = getTextContent(child);
          if (keywordValue) {
            keywords.push({ value: keywordValue, uri });
          } else if (uri) {
            throw new InternalParserError(
              "<stichwort> with an uri attribute needs a non-empty content",
            );
          }
        }
        break;
    }
  }

  if (!id) throw InternalParserError.missingValue("identifikation");
  if (!name) throw InternalParserError.missingValue("name");
  if (!lastChangedAt) throw InternalParserError.missingValue("letzteAenderung");
  if (!type) throw InternalParserError.missingValue("typ");

  const rule: Rule = {
    identifier: `${id}:${version || ""}`,
    id,
    version,
    name,
    description,
    freeFormDefinition,
    creator,
    lastChangedAt,
    type,
    script,
    normReferences,
    keywords,
  };

  context.rules.insert(rule);
  return rule;
}

function parseSchema(element: Element, context: Context): Schema {
  const container = createBaseContainer();
  parseBaseContainer(element, container);

  const data = parseBaseData(container);

  let label: string | undefined;
  let help: string | undefined;
  let ableitungsmodifikationenStruktur: any;
  let ableitungsmodifikationenRepraesentation: any;
  let dokumentsteckbrief: ElementIdentifikation | undefined;
  const rules: ElementIdentifikation[] = [];
  const children: ChildRef[] = [];

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "bezeichnung":
        label = getTextContent(child);
        break;
      case "hilfetext":
        help = getTextContent(child);
        break;
      case "ableitungsmodifikationenStruktur":
        ableitungsmodifikationenStruktur = parseCode(
          child,
          parseAbleitungsmodifikationenStruktur,
        );
        break;
      case "ableitungsmodifikationenRepraesentation":
        ableitungsmodifikationenRepraesentation = parseCode(
          child,
          parseAbleitungsmodifikationenRepraesentation,
        );
        break;
      case "dokumentsteckbrief":
        dokumentsteckbrief = parseIdentification(child);
        break;
      case "regel":
        {
          const rule = parseRule(child, context);
          rules.push({ id: rule.id, version: rule.version });
        }
        break;
      case "struktur":
        {
          const childRef = parseStructure(child, context);
          children.push(childRef);
        }
        break;
    }
  }

  if (!label) throw InternalParserError.missingValue("bezeichnung");
  if (!ableitungsmodifikationenStruktur)
    throw InternalParserError.missingValue("ableitungsmodifikationenStruktur");
  if (!ableitungsmodifikationenRepraesentation)
    throw InternalParserError.missingValue(
      "ableitungsmodifikationenRepraesentation",
    );
  if (!dokumentsteckbrief)
    throw InternalParserError.missingValue("dokumentsteckbrief");

  return {
    ...data,
    label,
    help,
    ableitungsmodifikationenStruktur,
    ableitungsmodifikationenRepraesentation,
    dokumentsteckbrief,
    rules,
    children,
  };
}

function parseHeader(element: Element): MessageHeader {
  let nachrichtId: string | undefined;
  let erstellungsZeitpunkt: Date | undefined;

  for (const child of Array.from(element.children)) {
    switch (child.localName) {
      case "nachrichtID":
        nachrichtId = getTextContent(child);
        break;
      case "erstellungszeitpunkt":
        erstellungsZeitpunkt = new Date(parseDate(getTextContent(child)));
        break;
    }
  }

  if (!nachrichtId) throw InternalParserError.missingValue("nachrichtID");
  if (!erstellungsZeitpunkt)
    throw InternalParserError.missingValue("erstellungszeitpunkt");

  return { nachrichtId, erstellungsZeitpunkt };
}

export function parseSchemaMessage(xmlString: string): SchemaMessage {
  const xmlDoc = parseXmlDocument(xmlString);

  // Create context
  const context: Context = {
    dataGroups: new Table(),
    dataFields: new Table(),
    rules: new Table(),
  };

  // Find the root element
  const rootElement = xmlDoc.documentElement;
  if (rootElement.localName !== SCHEMA_MESSAGE) {
    throw InternalParserError.unexpectedNode(rootElement.localName);
  }

  let header: MessageHeader | undefined;
  let schema: Schema | undefined;

  // Iterate over all children and process based on localName
  for (const child of Array.from(rootElement.children)) {
    switch (child.localName) {
      case "header":
        header = parseHeader(child);
        break;
      case "stammdatenschema":
        schema = parseSchema(child, context);
        break;
      default:
        // Ignore unknown elements or potentially log them
        console.log(`Ignoring unknown element: ${child.localName}`);
        break;
    }
  }

  // Ensure required elements were found
  if (!header) {
    throw InternalParserError.missingValue("header");
  }
  if (!schema) {
    throw InternalParserError.missingValue("stammdatenschema");
  }

  // Create and return the SchemaMessage
  return {
    header,
    schema,
    gruppen: context.dataGroups.entries(),
    felder: context.dataFields.entries(),
    regeln: context.rules.entries(),
  };
}
